import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Save, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { sarbRatesApi } from '../lib/api';

const RATE_INDICES = [
  { value: 'repo_rate', label: 'SARB Repo Rate', description: 'The repurchase rate at which SARB lends to commercial banks' },
  { value: 'zaronia', label: 'ZARONIA', description: 'South African Rand Overnight Index Average — replacing JIBAR by end 2026' },
  { value: 'prime_rate', label: 'Prime Rate', description: 'The prime lending rate (typically Repo + 3.5%)' },
  { value: 'custom', label: 'Custom Rate', description: 'A manually set fixed reference rate' },
];

const RateManagementPage = () => {
  const [config, setConfig] = useState(null);
  const [currentRates, setCurrentRates] = useState(null);
  const [ncaCaps, setNcaCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, ratesRes, ncaRes] = await Promise.all([
        api.get('/admin/rate-config'),
        sarbRatesApi.current(),
        api.get('/admin/nca-caps').catch(() => ({ data: null })),
      ]);
      setConfig(cfgRes.data);
      setCurrentRates(ratesRes.data);
      setNcaCaps(ncaRes.data);
    } catch (e) {
      setConfig({
        reference_rate_index: 'repo_rate',
        custom_rate: 8.25,
        auto_update_floating: true,
        nca_enforce_caps: false,
      });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/rate-config', config);
      toast.success('Rate configuration saved. All new floating-rate loans will use this index.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const getActiveRate = () => {
    if (!config || !currentRates) return null;
    const idx = config.reference_rate_index;
    if (idx === 'custom') return config.custom_rate;
    if (idx === 'repo_rate') return currentRates?.repo_rate?.rate;
    if (idx === 'zaronia') return currentRates?.zaronia?.rate;
    if (idx === 'prime_rate') return currentRates?.prime_rate?.rate || (currentRates?.repo_rate?.rate ? currentRates.repo_rate.rate + 3.5 : null);
    return null;
  };

  const selectedIndex = RATE_INDICES.find(r => r.value === config?.reference_rate_index);
  const activeRate = getActiveRate();

  if (loading) return <AdminSubPage title="Rate Management" breadcrumb={['Admin', 'System', 'Rate Management']} backPath="/dashboard/admin/system"><div className="text-center py-16 text-gray-400">Loading...</div></AdminSubPage>;

  return (
    <AdminSubPage title="Rate Management" breadcrumb={['Admin', 'System', 'Rate Management']} backPath="/dashboard/admin/system">
      <div className="space-y-6" data-testid="rate-management-page">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Configure the reference interest rate index used for all floating-rate loan calculations.</p>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1f1f1f] text-white" data-testid="save-rate-config-btn">
            {saving ? <Save className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>

        {/* Active Rate Display */}
        <div className="bg-[#1f1f1f] p-6 text-white" data-testid="active-rate-display">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Active Reference Rate</p>
              <p className="text-4xl font-bold">{activeRate != null ? `${activeRate}%` : 'Not set'}</p>
              <p className="text-sm text-gray-400 mt-2">{selectedIndex?.label || 'Unknown'}</p>
            </div>
            <div className="p-3 bg-white/10 rounded">
              <TrendingUp className="w-8 h-8 text-[#C5A059]" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Floating-rate formula: <span className="text-[#C5A059] font-medium">{selectedIndex?.label || 'Index'} ({activeRate ?? '?'}%) + Credit Score Spread = Effective Rate</span>
          </p>
        </div>

        {/* Configuration */}
        <div className="bg-white border border-gray-200 p-6 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Reference Rate Index</Label>
            <Select value={config?.reference_rate_index || 'repo_rate'} onValueChange={v => setConfig(p => ({ ...p, reference_rate_index: v }))}>
              <SelectTrigger className="mt-1" data-testid="rate-index-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RATE_INDICES.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedIndex && (
              <p className="text-xs text-gray-400 mt-2">{selectedIndex.description}</p>
            )}
          </div>

          {config?.reference_rate_index === 'custom' && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Custom Reference Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={config?.custom_rate || ''}
                onChange={e => setConfig(p => ({ ...p, custom_rate: parseFloat(e.target.value) || 0 }))}
                placeholder="e.g., 8.25"
                className="mt-1 w-48"
                data-testid="custom-rate-input"
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <Switch
              checked={config?.auto_update_floating !== false}
              onCheckedChange={v => setConfig(p => ({ ...p, auto_update_floating: v }))}
              data-testid="auto-update-toggle"
            />
            <div>
              <Label className="text-sm text-gray-700">Auto-recalculate floating loans</Label>
              <p className="text-xs text-gray-400">When the reference rate changes, automatically recalculate effective rates on all active floating-rate loans.</p>
            </div>
          </div>

          {/* NCA Rate-Cap Enforcement */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={config?.nca_enforce_caps === true}
                onCheckedChange={v => setConfig(p => ({ ...p, nca_enforce_caps: v }))}
                data-testid="nca-enforce-toggle"
              />
              <div>
                <Label className="text-sm text-gray-700">Enforce NCA Rate Caps <span className="text-[10px] uppercase tracking-wider text-amber-600 ml-2">National Credit Act</span></Label>
                <p className="text-xs text-gray-400">When enabled, loan creation rejects any rate exceeding the NCR-prescribed cap for the loan sub-sector. Default: <strong>off</strong>.</p>
              </div>
            </div>

            {ncaCaps?.caps?.length > 0 && (
              <div className="mt-4 border border-gray-200" data-testid="nca-cap-table">
                <div className="bg-gray-50 px-4 py-2 text-[10px] uppercase tracking-wider text-gray-500 flex items-center justify-between">
                  <span>Current NCA Caps (Annual)</span>
                  <span className={config?.nca_enforce_caps ? 'text-emerald-600 font-medium' : 'text-gray-400'}>{config?.nca_enforce_caps ? 'ENFORCED' : 'ADVISORY ONLY'}</span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-500 font-medium">Sub-sector</th>
                      <th className="px-4 py-2 text-left text-gray-500 font-medium">Formula</th>
                      <th className="px-4 py-2 text-right text-gray-500 font-medium">Current Cap</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ncaCaps.caps.map((c, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-gray-700">{c.applies_to}</td>
                        <td className="px-4 py-2 text-gray-500">{c.rule_type === 'repo_plus' ? `Repo + ${c.spread}%` : `${c.fixed_annual}% (fixed)`}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-[#1f1f1f]">{c.current_cap_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Current Market Rates Reference */}
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4">Current Market Rates</h3>
          <p className="text-xs text-gray-500 mb-3">These rates are managed under Reports &gt; Interest Rates. The selected index above determines which rate is used for floating-rate calculations.</p>
          <div className="grid grid-cols-3 gap-4">
            <div className={`p-4 border ${config?.reference_rate_index === 'repo_rate' ? 'border-[#C5A059] bg-amber-50' : 'border-gray-200'}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Repo Rate</p>
              <p className="text-2xl font-bold text-[#1f1f1f] mt-1">{currentRates?.repo_rate?.rate ?? '-'}%</p>
              <p className="text-xs text-gray-400 mt-1">{currentRates?.repo_rate?.effective_date || 'N/A'}</p>
              {config?.reference_rate_index === 'repo_rate' && <p className="text-xs text-[#C5A059] font-medium mt-2">Active index</p>}
            </div>
            <div className={`p-4 border ${config?.reference_rate_index === 'zaronia' ? 'border-[#C5A059] bg-amber-50' : 'border-gray-200'}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wider">ZARONIA</p>
              <p className="text-2xl font-bold text-[#1f1f1f] mt-1">{currentRates?.zaronia?.rate ?? '-'}%</p>
              <p className="text-xs text-gray-400 mt-1">{currentRates?.zaronia?.effective_date || 'N/A'}</p>
              {config?.reference_rate_index === 'zaronia' && <p className="text-xs text-[#C5A059] font-medium mt-2">Active index</p>}
            </div>
            <div className={`p-4 border ${config?.reference_rate_index === 'prime_rate' ? 'border-[#C5A059] bg-amber-50' : 'border-gray-200'}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Prime Rate</p>
              <p className="text-2xl font-bold text-[#1f1f1f] mt-1">{currentRates?.prime_rate?.rate ?? (currentRates?.repo_rate?.rate ? (currentRates.repo_rate.rate + 3.5).toFixed(2) : '-')}%</p>
              <p className="text-xs text-gray-400 mt-1">Repo + 3.5%</p>
              {config?.reference_rate_index === 'prime_rate' && <p className="text-xs text-[#C5A059] font-medium mt-2">Active index</p>}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-blue-50 border border-blue-200 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-700">
            <p className="font-medium">How this works</p>
            <p className="mt-1">When a floating-rate loan is created, the system fetches the current value of the selected reference rate index and adds the client's credit score spread. Changing the index here affects all <strong>new</strong> floating-rate loans. Existing loans retain their origination rate unless auto-recalculate is enabled.</p>
          </div>
        </div>
      </div>
    </AdminSubPage>
  );
};

export default RateManagementPage;
